import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { agentService } from "../services/AgentServices";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ManageAgent() {
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [agents, setagents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [totalAgents, setTotalAgents] = useState(0);
  const [filteredAgentsCount, setFilteredAgentsCount] = useState(0);
  const [currentPageAgentsCount, setCurrentPageAgentsCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, region, status]);

  // Fetch total agent count on component mount and when filters change
  useEffect(() => {
    fetchTotalAgentCount();
  }, [search, status]);

  // Fetch agents when filters change
  useEffect(() => {
    fetchagents();
  }, [currentPage, search, status]);

  // Calculate filtered agents count whenever agents change
  useEffect(() => {
    if (agents.length > 0) {
      const formattedagents = formatagents(agents);
      
      // Filter agents based on all criteria
      const filtered = formattedagents.filter((agent) => {
        const searchTerm = search.toLowerCase();
        
        const searchMatch =
          agent.id.toString().includes(searchTerm) ||
          agent.fullName.toLowerCase().includes(searchTerm) ||
          agent.email.toLowerCase().includes(searchTerm) ||
          agent.firstName.toLowerCase().includes(searchTerm) ||
          agent.surname.toLowerCase().includes(searchTerm);

        const regionMatch = region === "all" || 
          (agent.address.county && agent.address.county.toLowerCase() === region.toLowerCase());
        
        const statusMatch = status === "all" || 
          agent.status.toLowerCase() === status.toLowerCase();

        return searchMatch && statusMatch && regionMatch;
      });

      // Calculate paginated agents
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

      // Update statistics
      setFilteredAgentsCount(filtered.length);
      setCurrentPageAgentsCount(paginated.length);
    } else {
      setFilteredAgentsCount(0);
      setCurrentPageAgentsCount(0);
    }
  }, [agents, currentPage, search, region, status]);

  // Function to fetch total agent count with filters
  const fetchTotalAgentCount = async () => {
    try {
      // Prepare filters for the count API
      const filters = {};
      
      if (search) {
        filters.search = search;
      }
      
      if (status !== 'all') {
        filters.status = status;
      }
      
      const response = await agentService.getAgentCount(filters);
      
      // Assuming the API returns { count: number } or similar structure
      if (response.count !== undefined) {
        setTotalAgents(response.count);
      } else if (response.total !== undefined) {
        setTotalAgents(response.total);
      } else {
        // If the response structure is different, try to extract count from the first property
        const count = Object.values(response)[0];
        if (typeof count === 'number') {
          setTotalAgents(count);
        } else {
          console.warn('Unexpected API response format:', response);
          // Fallback: fetch count from pagination in fetchagents
        }
      }
    } catch (error) {
      console.error('Error fetching agent count:', error);
      // Don't show error to user for count failure, use fallback
    }
  };

  const fetchagents = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 6,
        ...(status !== 'all' && { status }),
        ...(search && { search })
      };

      const response = await agentService.getAllagents(params);
      
      if (response.users && Array.isArray(response.users)) {
        setagents(response.users);
        
        // Extract unique regions (counties) from the response
        const regions = [...new Set(response.users
          .map(agent => agent.region || agent.county)
          .filter(region => region && region.trim() !== '')
        )].sort();
        
        setAvailableRegions(regions);
        
        // Use pagination total from API as fallback if getAgentCount failed
        if (response.pagination && response.pagination.total && totalAgents === 0) {
          setTotalAgents(response.pagination.total);
        }
      } else {
        setagents([]);
        setAvailableRegions([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setSnackMessage('Failed to load agents');
      setSnackOpen(true);
      setagents([]);
      setAvailableRegions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => setSnackOpen(false);

  const goToCreateAccount = () => {
    navigate("/create-agent-account");
  };

  // Format API data to match your existing structure
  const formatagents = (apiagents) => {
    return apiagents.map(agent => ({
      agent_id: agent.agent_id,
      id: agent.Customer_id,
      fullName: `${agent.first_name} ${agent.last_name}`,
      firstName: agent.first_name,
      surname: agent.last_name,
      name: agent.first_name+" "+agent.last_name,
      email: agent.email,
      mobile: agent.phone_number,
      role: agent.role,
      address: {
        line1: agent.address_line1 || '',
        line2: agent.address_line2 || '',
        line3: agent.address_line3 || '',
        city: agent.city || '',
        county: agent.region || 'N/A',
        country: agent.country || 'N/A',
        postcode: agent.postcode || ''
      },
      status: agent.current_status ? agent.current_status.charAt(0).toUpperCase() + agent.current_status.slice(1) : 'Awaiting',
      notes: agent.status_note || '',
      value: `${agent.wallet_details?.total_points || 0} TopCoin`,
      wallet_details: agent.wallet_details,
      transaction_summary: agent.transaction_summary,
      recent_transactions: agent.recent_transactions || [],
      referrer_name: agent.referer_name || 'N/A',
      referrer_id: agent.referer_id || 'N/A',
      referrer_agent_type: agent.referer_agent_role || 'N/A',
      referrer_country: agent.referer_country || 'N/A',
      referrer_region: agent.referer_region || 'N/A',
      referrer_email: agent.referer_email || 'N/A',
      referrer_phone: agent.referer_phone_number || 'N/A',
    }));
  };

  const handleRowClick = (agent) => {
    navigate("/agent-details", { state: { agent } });
  };

  // Use formatted agents from API, fallback to empty array
  const formattedagents = agents.length > 0 ? formatagents(agents) : [];
  
  // Enhanced search logic for ID, name, and email
  const filteredagents = formattedagents.filter((agent) => {
    const searchTerm = search.toLowerCase();
    
    const searchMatch =
      agent.id.toString().includes(searchTerm) ||
      agent.fullName.toLowerCase().includes(searchTerm) ||
      agent.email.toLowerCase().includes(searchTerm) ||
      agent.firstName.toLowerCase().includes(searchTerm) ||
      agent.surname.toLowerCase().includes(searchTerm);

    const regionMatch = region === "all" || 
      (agent.address.county && agent.address.county.toLowerCase() === region.toLowerCase());
    
    const statusMatch = status === "all" || 
      agent.status.toLowerCase() === status.toLowerCase();

    return searchMatch && statusMatch && regionMatch;
  });

  const itemsPerPage = 6;
  const totalPages = Math.ceil(filteredagents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedagents = filteredagents.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Retail Advertising Business Agent</h2>
            <button className="action-btn" onClick={goToCreateAccount}>+ Add New Agent</button>
          </div>

          {/* Agent Statistics Section - Added similar to agent version */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Agents:</span>
              <span className="stat-value">{totalAgents}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Agents:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {paginatedagents.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by region:</label>
              <select value={region} onChange={(e) => setRegion(e.target.value)} className="filter-select">
                <option value="all">All Regions</option>
                {availableRegions.map((regionName, index) => (
                  <option key={index} value={regionName.toLowerCase()}>
                    {regionName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by status:</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="filter-select">
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="awaiting">Awaiting</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "40px" }}>ID</th>
                <th style={{ width: "250px" }}>Full Name</th>
                <th>Agent Type</th>
                <th style={{ width: "250px" }}>Email</th>
                <th style={{ width: "250px" }}>County</th>
                <th style={{ width: "250px" }}>Country</th>
                <th style={{ width: "120px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Agents...</div>
                  </td>
                </tr>
              ) : paginatedagents.length > 0 ? (
                paginatedagents.map((agent, i) => (
                  <tr 
                    key={agent.id || i}
                    onClick={() => handleRowClick(agent)}
                  >
                    <td>{agent.id}</td>
                    <td>{agent.fullName}</td>

                    <td>{agent.role === "SeniorBusinessAdsAgent" ? "Senior Business Ads Agent" : agent.role === "MiniBusinessAdsAgent" ? "Mini Business Ads Agent" : agent.role}</td>
                    <td>{agent.email}</td>
                    <td>{agent.address.county}</td>
                    <td>{agent.address.country}</td>
                    <td>
                      <span className={`status ${agent.status.toLowerCase()}`}>{agent.status}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {filteredAgentsCount === 0 && totalAgents > 0 
                      ? "No agents match your filters" 
                      : "No agents found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && filteredAgentsCount > 0 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar 
        open={snackOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          severity="success"
          icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
          sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: '25px',
              width: '200px',
            }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageAgent;